import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import RevenueMetricsCard from './components/RevenueMetricsCard';
import RevenueChart from './components/RevenueChart';
import SubscriptionPlanChart from './components/SubscriptionPlanChart';
import RenewalPipelineChart from './components/RenewalPipelineChart';
import BillingAnalyticsTable from './components/BillingAnalyticsTable';

const RevenueIntelligenceDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('current_quarter');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [comparisonMode, setComparisonMode] = useState('previous_period');

  const periodOptions = [
    { value: 'current_month', label: 'Current Month' },
    { value: 'current_quarter', label: 'Current Quarter' },
    { value: 'current_year', label: 'Current Year' },
    { value: 'last_12_months', label: 'Last 12 Months' }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'CAD', label: 'CAD (C$)' }
  ];

  const comparisonOptions = [
    { value: 'previous_period', label: 'Previous Period' },
    { value: 'year_over_year', label: 'Year over Year' },
    { value: 'quarter_over_quarter', label: 'Quarter over Quarter' }
  ];

  const revenueMetrics = [
    {
      title: 'Monthly Recurring Revenue',
      value: '308,000',
      change: '+6.6%',
      changeType: 'positive',
      icon: 'TrendingUp',
      currency: '$'
    },
    {
      title: 'Annual Run Rate',
      value: '3.7M',
      change: '+112.4%',
      changeType: 'positive',
      icon: 'BarChart3',
      currency: '$'
    },
    {
      title: 'Trial Conversion Rate',
      value: '24.8',
      change: '+2.1%',
      changeType: 'positive',
      icon: 'Users',
      currency: ''
    },
    {
      title: 'Average Deal Size',
      value: '12,450',
      change: '+8.3%',
      changeType: 'positive',
      icon: 'DollarSign',
      currency: '$'
    },
    {
      title: 'Payment Failure Rate',
      value: '5.8',
      change: '-1.2%',
      changeType: 'positive',
      icon: 'AlertTriangle',
      currency: ''
    },
    {
      title: 'Revenue Churn',
      value: '4.2',
      change: '-0.8%',
      changeType: 'positive',
      icon: 'TrendingDown',
      currency: ''
    }
  ];

  const handleExportData = () => {
    // Mock export functionality
    console.log('Exporting revenue data...');
  };

  const handleScheduleReport = () => {
    // Mock schedule report functionality
    console.log('Scheduling automated report...');
  };

  return (
    <>
      <Helmet>
        <title>Revenue Intelligence Dashboard - SuperAdmin</title>
        <meta name="description" content="Comprehensive financial analytics and revenue intelligence for executive decision-making" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar />
        
        <main className="lg:ml-60 pt-16 lg:pb-0 pb-20">
          <div className="p-6 space-y-6">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-text-primary mb-2">Revenue Intelligence</h1>
                <p className="text-text-secondary">
                  Comprehensive financial analytics and revenue performance tracking
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center space-x-3">
                  <Select
                    options={periodOptions}
                    value={selectedPeriod}
                    onChange={setSelectedPeriod}
                    placeholder="Select period"
                    className="w-40"
                  />
                  
                  <Select
                    options={currencyOptions}
                    value={selectedCurrency}
                    onChange={setSelectedCurrency}
                    placeholder="Currency"
                    className="w-32"
                  />
                  
                  <Select
                    options={comparisonOptions}
                    value={comparisonMode}
                    onChange={setComparisonMode}
                    placeholder="Compare to"
                    className="w-44"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleExportData}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Export
                  </Button>
                  
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={handleScheduleReport}
                    iconName="Calendar"
                    iconPosition="left"
                  >
                    Schedule Report
                  </Button>
                </div>
              </div>
            </div>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {revenueMetrics.map((metric, index) => (
                <RevenueMetricsCard
                  key={index}
                  title={metric.title}
                  value={metric.value}
                  change={metric.change}
                  changeType={metric.changeType}
                  icon={metric.icon}
                  currency={metric.currency}
                />
              ))}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-16 gap-6">
              {/* Revenue Chart - 11 columns */}
              <div className="xl:col-span-11">
                <RevenueChart />
              </div>
              
              {/* Subscription Plan Chart - 5 columns */}
              <div className="xl:col-span-5">
                <SubscriptionPlanChart />
              </div>
            </div>

            {/* Secondary Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Renewal Pipeline */}
              <div>
                <RenewalPipelineChart />
              </div>
              
              {/* Quick Stats Card */}
              <div className="bg-card border border-border rounded-lg p-6 dashboard-fade-in">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-1">Financial Health</h3>
                    <p className="text-text-secondary text-sm">Key financial indicators</p>
                  </div>
                  <Icon name="TrendingUp" size={20} className="text-success" />
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                        <Icon name="DollarSign" size={16} className="text-success" />
                      </div>
                      <div>
                        <p className="text-text-primary font-medium text-sm">Cash Flow</p>
                        <p className="text-text-secondary text-xs">Monthly net revenue</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-text-primary font-bold">$284K</p>
                      <p className="text-success text-xs">+12.4%</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="Target" size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-text-primary font-medium text-sm">LTV/CAC Ratio</p>
                        <p className="text-text-secondary text-xs">Customer value efficiency</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-text-primary font-bold">3.2x</p>
                      <p className="text-success text-xs">+0.3x</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
                        <Icon name="Clock" size={16} className="text-warning" />
                      </div>
                      <div>
                        <p className="text-text-primary font-medium text-sm">Payback Period</p>
                        <p className="text-text-secondary text-xs">Customer acquisition ROI</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-text-primary font-bold">14 months</p>
                      <p className="text-success text-xs">-2 months</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
                        <Icon name="Percent" size={16} className="text-secondary" />
                      </div>
                      <div>
                        <p className="text-text-primary font-medium text-sm">Gross Margin</p>
                        <p className="text-text-secondary text-xs">Revenue efficiency</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-text-primary font-bold">78.5%</p>
                      <p className="text-success text-xs">+1.2%</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-border">
                  <Button variant="outline" size="sm" fullWidth iconName="BarChart3" iconPosition="left">
                    View Detailed Analysis
                  </Button>
                </div>
              </div>
            </div>

            {/* Billing Analytics Table */}
            <div>
              <BillingAnalyticsTable />
            </div>

            {/* Predictive Analytics Section */}
            <div className="bg-card border border-border rounded-lg p-6 dashboard-fade-in">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-1">Revenue Forecasting</h3>
                  <p className="text-text-secondary text-sm">AI-powered revenue predictions with confidence intervals</p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-success rounded-full pulse-alert"></div>
                  <span className="text-text-secondary text-sm">Live Predictions</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="TrendingUp" size={24} className="text-primary" />
                  </div>
                  <p className="text-text-secondary text-sm mb-1">Next Quarter Forecast</p>
                  <p className="text-text-primary font-bold text-xl">$1.2M</p>
                  <p className="text-success text-sm">95% confidence</p>
                </div>
                
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Target" size={24} className="text-secondary" />
                  </div>
                  <p className="text-text-secondary text-sm mb-1">Annual Target Progress</p>
                  <p className="text-text-primary font-bold text-xl">68%</p>
                  <p className="text-secondary text-sm">On track</p>
                </div>
                
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="AlertTriangle" size={24} className="text-warning" />
                  </div>
                  <p className="text-text-secondary text-sm mb-1">Risk Assessment</p>
                  <p className="text-text-primary font-bold text-xl">Low</p>
                  <p className="text-warning text-sm">Monitor churn</p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default RevenueIntelligenceDashboard;